import * as React from 'react';
import { Modal, Button, Grid, XFilledIcon } from 'react-unity';
import { Alert} from '../../../common';
import '../../../../components/PortOpeningRequests/components/porRule/RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import {ToggleField} from '../../../common/';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './OnPremAbstractRulesForm';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import SelectField from '../../../common/form-controls/SelectField';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import ObjectLookup from '../../../common/form-controls/Lookups/ObjectLookup';
import OnPremPortOpeningObject from '../../../../models/entities/OnPremPortOpeningObject';

interface RuleModalCertifiedConnectionProps extends RulesFormProps {}

interface SubToOnPremRuleStateForm extends IRuleStateForm {
	name: string;
	sourceEnvironment: string;
	sourceIPs: string;
	destinationURLs: string;
	destinationEnvironment: string;
	protocol: string;
	sourceObjectsList: OnPremPortOpeningObject[];
    destinationObjectsList: OnPremPortOpeningObject[];
}
interface RuleModalCertifiedConnectionState {
	stateForm: SubToOnPremRuleStateForm;
}

export default class RuleModalCertifiedConnection extends AbstractRulesForm<
	RuleModalCertifiedConnectionProps,
	RuleModalCertifiedConnectionState
> {
	constructor(props: RuleModalCertifiedConnectionProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
		};

		this.initFormModel();
	}

	isURLValid = (url) => /^(https?:\/\/)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s:]*)?$/.test(url);

	initFormModel() {
		super.initFormModel();

		this.formModel.addField('sourceEnvironment', {
			getValue: () => this.state.stateForm.sourceEnvironment,
			validation: {
				required: true,
			},
		});

		this.formModel.addField('destinationEnvironment', {
			getValue: () => this.state.stateForm.destinationEnvironment,
			validation: {
				required: true,
			},
		});

		this.formModel.fields.sourceIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false,
			})
		);

		this.formModel.fields.destinationIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false,
			})
		);

		this.formModel.fields.sourceIPs.validation.required = () =>
			!(this.state.stateForm.sourceObjects?.trim().length > 0);

		this.formModel.fields.sourceObjects.validation.required = () =>
			!(this.state.stateForm.sourceIPs?.trim().length > 0);

		this.formModel.fields.destinationIPs.validation.required = () =>
			!(this.state.stateForm.destinationObjects?.trim().length > 0);

		this.formModel.fields.destinationObjects.validation.required = () =>
			!(this.state.stateForm.destinationIPs?.trim().length > 0);

		this.formModel.addField('protocol', {
			getValue: () => this.state.stateForm.protocol,
			validation: {
				required: true,
			},
		});

		this.formModel.fields.destinationPorts.validation.rules.push(
			this.PortInputValidationRule({
				minimumPort: 0,
			})
		);
	}

	getDefaultFormState() {
		return {
			operation: RuleOperation.Add,
			action: RuleAction.Allow,
			sslBypass: 'false',
			name: '',
			protocol: '',
			sourceObjectsList: [],
            destinationObjectsList: [],
		};
	}

	// eslint-disable-next-line class-methods-use-this
	parseObjectsList(value: string): OnPremPortOpeningObject[] {
        return value
            ? value.split(',').filter(n => n.trim()).map((name, idx) => ({
                id: -(idx + 1),
                name: name.trim(),
            } as OnPremPortOpeningObject))
            : [];
    }

	getRuleCopy() {
		const copy = super.getRuleCopy() as unknown as SubToOnPremRuleStateForm;
		return {
			...copy,
			sourceObjectsList: this.parseObjectsList(copy.sourceObjects),
			destinationObjectsList: this.parseObjectsList(copy.destinationObjects),
		} as unknown as ReturnType<AbstractRulesForm<RuleModalCertifiedConnectionProps, RuleModalCertifiedConnectionState>['getRuleCopy']>;
	}

    handleObjectSelection = (
        field: 'sourceObjects' | 'destinationObjects',
        listField: 'sourceObjectsList' | 'destinationObjectsList',
        obj: OnPremPortOpeningObject | null
    ) => {
        if (!obj) return;
        const list = this.state.stateForm[listField] ?? [];
        if (list.find(o => o.name === obj.name)) return;

        const updatedList = [...list, obj];
        this.handleStateFormChange(
            [field, listField],
            [updatedList.map(o => o.name).join(','), updatedList]
        );
    };

    handleRemoveObject = (
        field: 'sourceObjects' | 'destinationObjects',
        listField: 'sourceObjectsList' | 'destinationObjectsList',
        objName: string
    ) => {
        const updatedList = (this.state.stateForm[listField] ?? []).filter(o => o.name !== objName);
        this.handleStateFormChange(
            [field, listField],
            [updatedList.map(o => o.name).join(','), updatedList]
        );
    };

	resetIPsValidations() {
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	wasEditted() {
		if (this.props.editingRule) {
			return (
				this.props.editingRule.description != this.stateFormHandler().description.value ||
				this.props.editingRule.action.value != this.stateFormHandler().action.value ||
				this.props.editingRule.sourceIPs != this.state.stateForm.sourceIPs ||
				this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value ||
				this.props.editingRule.destinationPorts != this.stateFormHandler().destinationPorts.value ||
				this.props.editingRule.destinationURLs != this.state.stateForm.destinationURLs ||
				this.props.editingRule.protocol != this.state.stateForm.protocol ||
				this.props.editingRule.operation != this.state.stateForm.operation ||
				this.props.editingRule.sourceObjects != this.state.stateForm.sourceObjects ||
				this.props.editingRule.destinationObjects != this.state.stateForm.destinationObjects ||
				this.props.editingRule.destinationEnvironment != this.state.stateForm.destinationEnvironment ||
				this.props.editingRule.sourceEnvironment != this.state.stateForm.sourceEnvironment
			);
		}
		return true;
	}

	renderObjectsLookup(
	label: string,
	field: 'sourceObjects' | 'destinationObjects',
	listField: 'sourceObjectsList' | 'destinationObjectsList'
    ) {
        const list = this.state.stateForm[listField] ?? [];
        return (
            <div>
                <label className="em-c-field__label">{label}</label>
                {this.formCanBeEdited() && (
                    <ObjectLookup
                        label=""
                        placeholder="Search for objects by name..."
                        value={null}
                        onChange={(obj) => this.handleObjectSelection(field, listField, obj)}
                        note="For pre-existing firewall objects only. Type at least 3 characters to search."
                    />
                )}
                {list.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                        {list.map((obj) => (
                            <div
                                key={obj.name}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 8px',
                                    margin: '4px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#f5f5f5',
                                }}
                            >
                                <span style={{ marginRight: '8px' }}>{obj.name}</span>
                                {this.formCanBeEdited() && (
                                    <button
                                        type="button"
                                        onClick={() => this.handleRemoveObject(field, listField, obj.name)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#cc0000',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

	render() {
		return (
			<Modal show={this.props.visible} onHide={() => {}}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>
							{this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
						</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size="small" />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						{!this.isModalForNewRule() && (
							<ToggleField
								{...this.stateFormHandler().operation}
								id='operation'
								label="Operation"
								disabled={!this.formCanBeEdited()}
								value={this.state.stateForm.operation?.value}
								options={
									this.props.isReadOnly
										? [new RuleOperation().fromValue(this.stateFormHandler().operation.value)]
										: [RuleOperation.Modify, RuleOperation.Remove]
								}
							/>
						)}
						<Grid variant="4-up">
							<Grid.Item>
								<ToggleField
									{...this.stateFormHandler().isTemporary}
									value={this.state.stateForm.isTemporary?.toString()}
									options={
										this.formCanBeEdited()
											? [
													{ text: 'Temporary', value: 'true' },
													{ text: 'Permanent', value: 'false' },
												]
											: this.state.stateForm.isTemporary
												? [{ text: 'Temporary', value: 'true' }]
												: [{ text: 'Permanent', value: 'false' }]
									}
									id='type'
									label="Type"
									disabled={
										this.state.stateForm.operation?.name !== RuleOperation.Add.name ||
										!this.formCanBeEdited()
									}
								/>
							</Grid.Item>
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={(event) => {
										this.handleStateFormChange('name', event.target.value);
									}}
									disabled={
										(this.state.stateForm.operation?.name !== RuleOperation.Add.name &&
											this.state.stateForm.operation?.name !== RuleOperation.Remove.name) ||
										!this.formCanBeEdited()
									}
								/>
							</Grid.Item>
						</Grid>
						<>
							<Grid variant="2-up">
								<Grid.Item>
									<ToggleField
										{...this.stateFormHandler().action}
										options={
											(this.formCanBeEdited() || this.stateFormHandler().action?.value === undefined)
												? [RuleAction.Allow, RuleAction.Block]
												: [new RuleAction().fromValue(this.stateFormHandler().action.value)]}
										id='action'
										label="Action"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
                                    {this.renderObjectsLookup('Source Objects/Host Name', 'sourceObjects', 'sourceObjectsList')}
                                </Grid.Item>
								<Grid.Item>
									<TextAreaField
										value={this.state.stateForm.sourceIPs}
										label="Source (IPs / IP Range / CIDR)"
										validation={this.formModel.fields.sourceIPs.validation}
										note="Multiple values are allowed, separated by semicolons."
										onChange={(event) => {
											const sanitizedValue = event.target.value.replace(/\n/g, '');
											this.handleStateFormChange('sourceIPs', sanitizedValue);
										}}
										disabled={!this.formCanBeEdited()}
									/>
									{this.assertSourceIPSContainsLargeSubnet() ? (
										<Alert
											variant="warning"
											body={this.getlargeSubnetAlerttext()}>
										</Alert>
									) : (
										<></>
									)}
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().destinationIPs}
										label="Destination (IPs / IP Range / CIDR)"
										onChange={(event) => {
											const sanitizedValue = event.target.value.replace(/\n/g, '');
											this.handleStateFormChange('destinationIPs', sanitizedValue);
										}}
										disabled={!this.formCanBeEdited()}
									/>
									{this.assertDestinationIPSContainsLargeSubnet() ? (
										<Alert
											variant="warning"
											body={this.getlargeSubnetAlerttext()}>
										</Alert>
									) : (
										<></>
									)}
								</Grid.Item>
								<Grid.Item>
                                    {this.renderObjectsLookup('Destination Objects/Host Name', 'destinationObjects', 'destinationObjectsList')}
                                </Grid.Item>
							</Grid>
							<Grid>
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().destinationPorts}
										label="Destination Ports"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<SelectField
										label={'Source Environment'}
										value={this.state.stateForm.sourceEnvironment}
										validation={this.formModel.fields.sourceEnvironment.validation}
										className="full-width"
										onChange={(event) => {
											this.handleStateFormChange('sourceEnvironment', event.target.value);
										}}
										disabled={!this.formCanBeEdited()}
										options={[
											{ value: 'L4', text: 'L4' },
											{ value: 'L3', text: 'L3' },
											{ value: 'Lablan', text: 'Lablan' },
											{ value: 'MITAS', text: 'MITAS' },
											{ value: 'XTO', text: 'XTO' },
											{ value: 'Other', text: 'Other' },
										]}
									/>
								</Grid.Item>
								<Grid.Item>
									<SelectField
										label={'Destination Environment'}
										value={this.state.stateForm.destinationEnvironment}
										validation={this.formModel.fields.destinationEnvironment.validation}
										className="full-width"
										onChange={(event) => {
											this.handleStateFormChange('destinationEnvironment', event.target.value);
										}}
										disabled={!this.formCanBeEdited()}
										options={['L4', 'L3', 'Lablan', 'MITAS', 'XTO', 'Other']}
									/>
								</Grid.Item>
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().description}
										label="Description"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
								<Grid.Item>
									<SelectField
										label="Protocol"
										value={this.state.stateForm.protocol}
										options={['TCP', 'UDP', 'ICMP echo-request', 'ICMP echo-reply']}
										onChange={(event) => this.handleStateFormChange('protocol', event.target.value)}
										validation={this.formModel.fields.protocol.validation}
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
						</>
					</Modal.Window.Body>
					{!this.props.isReadOnly && (
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={!this.formModel.isValid() || !this.wasEditted()}
										onClick={this.handleRequest}
									>
										{this.props.editingRule ? 'Confirm' : 'Create'}
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button variant="secondary" onClick={this.handleCancel}>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					)}
				</Modal.Window>
			</Modal>
		);
	}
}
